import { useState, useEffect, useMemo } from 'react';
import {
  RobotIcon,
  PaperPlaneRightIcon,
  CircleNotchIcon,
  EnvelopeIcon,
  UsersIcon,
  AtIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { MarkdownWYSIWYGEditor } from '@/components/common/MarkdownWYSIWYGEditor';

/**
 * Convert HTML content to plain text for textarea display.
 * Preserves newlines from <br> and </p> tags.
 */
function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  return (
    html
      // Handle <br>\n pattern (common from formatEmailBodyAsHtml) - treat as single newline
      .replace(/<br\s*\/?>\s*\n/gi, '\n')
      // Handle remaining <br> tags
      .replace(/<br\s*\/?>/gi, '\n')
      // Handle paragraph breaks
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      // Handle closing </p> (but not if it's at the very end)
      .replace(/<\/p>(?!$)/gi, '\n')
      // Strip all remaining HTML tags (including final </p>)
      .replace(/<[^>]+>/g, '')
      // Decode common HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      // Collapse multiple consecutive newlines to max 2
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

/**
 * Convert plain text to HTML for email sending.
 * Converts newlines to <br> tags.
 */
function plainTextToHtml(text: string): string {
  if (!text) return '';
  // Escape HTML entities and convert newlines to <br>
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped.split('\n').join('<br>');
}

export interface EmailDraft {
  id: string;
  to: string;
  cc?: string | null;
  subject: string;
  content: string;
  htmlContent?: string | null;
  isAiGenerated: boolean;
  status: string;
  sendingMailbox?: string | null;
}

interface EmailDraftEditorProps {
  draft: EmailDraft;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (updates: {
    subject?: string;
    htmlContent?: string;
    to?: string[];
    cc?: string[];
  }) => Promise<void>;
  onSend: () => Promise<void>;
  onDiscard: () => void;
  isSaving?: boolean;
  isSending?: boolean;
  mode?: 'html' | 'markdown';
}

export function EmailDraftEditor({
  draft,
  open,
  onOpenChange,
  onSave,
  onSend,
  onDiscard,
  isSaving = false,
  isSending = false,
  mode = 'html',
}: EmailDraftEditorProps) {
  // Convert HTML content to plain text for editing (HTML mode)
  // Or use htmlContent directly for Markdown mode
  // Prefer htmlContent (which has proper formatting) over plain content
  const initialContent = useMemo(() => {
    if (mode === 'markdown') {
      return draft.htmlContent || draft.content;
    }
    if (draft.htmlContent) {
      return htmlToPlainText(draft.htmlContent);
    }
    return draft.content;
  }, [mode, draft.htmlContent, draft.content]);

  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc || '');
  const [subject, setSubject] = useState(draft.subject);
  const [content, setContent] = useState(initialContent);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when switching to a DIFFERENT draft (by ID)
  // Using draft.id prevents flicker when the same draft object is updated
  useEffect(() => {
    setTo(draft.to);
    setCc(draft.cc || '');
    setSubject(draft.subject);
    if (mode === 'markdown') {
      setContent(draft.htmlContent || draft.content);
    } else {
      setContent(
        draft.htmlContent ? htmlToPlainText(draft.htmlContent) : draft.content,
      );
    }
    setHasChanges(false);
  }, [
    mode,
    draft.id,
    draft.to,
    draft.cc,
    draft.subject,
    draft.htmlContent,
    draft.content,
  ]); // Only reset when draft ID changes, not on every draft object update

  // Track changes - compare against initial values
  useEffect(() => {
    const originalContent =
      mode === 'markdown'
        ? draft.htmlContent || draft.content
        : draft.htmlContent
          ? htmlToPlainText(draft.htmlContent)
          : draft.content;
    const changed =
      to !== draft.to ||
      cc !== (draft.cc || '') ||
      subject !== draft.subject ||
      content !== originalContent;
    setHasChanges(changed);
  }, [
    mode,
    to,
    cc,
    subject,
    content,
    draft.to,
    draft.cc,
    draft.subject,
    draft.htmlContent,
    draft.content,
  ]);

  const handleSave = async () => {
    const updates: {
      subject?: string;
      htmlContent?: string;
      to?: string[];
      cc?: string[];
    } = {};

    if (subject !== draft.subject) {
      updates.subject = subject;
    }

    // Check if content has changed from the original
    const originalContent =
      mode === 'markdown'
        ? draft.htmlContent || draft.content
        : draft.htmlContent
          ? htmlToPlainText(draft.htmlContent)
          : draft.content;
    if (content !== originalContent) {
      if (mode === 'markdown') {
        updates.htmlContent = content;
      } else {
        updates.htmlContent = plainTextToHtml(content);
      }
    }

    if (to !== draft.to) {
      updates.to = to
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
    }
    if (cc !== (draft.cc || '')) {
      updates.cc = cc
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
    }

    await onSave(updates);
    setHasChanges(false);
  };

  const handleSend = async () => {
    // Save any pending changes first
    if (hasChanges) {
      await handleSave();
    }
    await onSend();
  };

  const isLoading = isSaving || isSending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="z-[1600] flex w-full flex-col gap-0 p-0 sm:max-w-xl [&>button.absolute]:hidden"
        overlayClassName="z-[1600]"
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <SheetHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 dark:from-blue-950/30 dark:to-indigo-950/30">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
                <EnvelopeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span>Compose Reply</span>
            </SheetTitle>
            {draft.isAiGenerated && (
              <Badge
                variant="secondary"
                className="gap-1.5 bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300"
              >
                <RobotIcon className="h-3 w-3" />
                AI Draft
              </Badge>
            )}
          </div>
        </SheetHeader>

        {/* Email Form */}
        <div className="flex flex-1 flex-col overflow-y-auto">
          {/* Recipients Section */}
          <div className="space-y-0 border-b bg-gray-50/50 dark:bg-gray-900/20">
            {/* From Field - Read-only */}
            {draft.sendingMailbox && (
              <div className="flex items-center border-b border-gray-100 px-6 py-2 dark:border-gray-800">
                <div className="flex w-16 shrink-0 items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                  <EnvelopeIcon className="h-3.5 w-3.5" />
                  From
                </div>
                <span className="text-sm text-foreground">
                  {draft.sendingMailbox}
                </span>
              </div>
            )}

            {/* To Field */}
            <div className="flex items-center border-b border-gray-100 px-6 py-2 dark:border-gray-800">
              <div className="flex w-16 shrink-0 items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <AtIcon className="h-3.5 w-3.5" />
                To
              </div>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="recipient@example.com"
                disabled={isLoading}
                className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              />
            </div>

            {/* CC Field */}
            <div className="flex items-center border-b border-gray-100 px-6 py-2 dark:border-gray-800">
              <div className="flex w-16 shrink-0 items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <UsersIcon className="h-3.5 w-3.5" />
                Cc
              </div>
              <Input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="Add recipients..."
                disabled={isLoading}
                className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              />
            </div>

            {/* Subject Field */}
            <div className="flex items-center px-6 py-2">
              <div className="w-16 shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">
                Subject
              </div>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                disabled={isLoading}
                className="border-0 bg-transparent px-0 font-medium shadow-none focus-visible:ring-0"
              />
            </div>
          </div>

          {/* Message Body */}
          <div className="flex flex-1 px-6 py-4">
            {mode === 'markdown' ? (
              <div className="w-full h-full">
                <MarkdownWYSIWYGEditor
                  initialValue={content}
                  onChange={setContent}
                  placeholder="Write your message..."
                  height={400}
                  showToolbar={true}
                  readonly={isLoading}
                />
              </div>
            ) : (
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your message..."
                disabled={isLoading}
                className={cn(
                  'h-full flex-1 resize-none border-0 bg-transparent p-0 text-[15px] leading-relaxed shadow-none',
                  'placeholder:text-gray-400 focus-visible:ring-0',
                )}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="flex-row items-center justify-between border-t bg-gray-50 px-6 py-4 dark:bg-gray-900/20">
          <Button
            variant="ghost"
            onClick={onDiscard}
            disabled={isLoading}
            size="sm"
            className="text-gray-500 hover:text-gray-700"
          >
            Close
          </Button>

          <div className="flex items-center gap-2">
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={isLoading}
                size="sm"
              >
                {isSaving && (
                  <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Save Draft
              </Button>
            )}
            <Button
              onClick={handleSend}
              disabled={isLoading}
              size="sm"
              className="bg-blue-600 px-4 hover:bg-blue-700"
            >
              {isSending ? (
                <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <PaperPlaneRightIcon className="mr-1.5 h-3.5 w-3.5" />
              )}
              Send
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
